import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux'; // Import useSelector
import { selectIsAuthenticated, selectCurrentUser } from '../../features/auth/authSlice'; // Import auth selectors
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Pagination from '../../components/ui/Pagination';
import HotelFilters from './components/HotelFilters';
import { useGetHotelsQuery } from '../../features/api/apiSlice'; // Removed useGetUserDataQuery
import { useToast } from '../../components/ui/Toast'; // Import useToast

const Hotels = () => {
  const navigate = useNavigate();
  const addToast = useToast(); // Initialize useToast

  // Get user and authentication status from Redux store
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectCurrentUser); // Get user from Redux store

  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    name: '',
    area: '',
    priceRange: [0, 1000],
    rating: '',
    amenities: [],
    roomType: '',
    availability: 'all'
  });

  const { data: hotelsData, isLoading, isSuccess, isError, error } = useGetHotelsQuery({
    offset: (currentPage - 1) * 8, // Assuming 8 items per page
    limit: 8,
    // Pass filters as API parameters
    city: filters.area, // Map 'area' filter to 'city' API parameter
    min_price: filters.priceRange[0],
    max_price: filters.priceRange[1],
    // Note: The backend API for get_hotels might need to be extended to support all these filters
  });

  const hotels = hotelsData?.data || [];
  const totalResults = hotelsData?.total_count || 0;
  const itemsPerPage = 8; // This should match the limit in useGetHotelsQuery
  const totalPages = Math.ceil(totalResults / itemsPerPage);

  const [selectedHotel, setSelectedHotel] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Handle API errors with toasts
  useEffect(() => {
    if (isError) {
      addToast(`Error fetching hotels: ${error?.message || 'Unknown error'}`, 'error');
    }
  }, [isError, error, addToast]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleBookNow = (hotel) => {
    if (!isAuthenticated) {
      addToast('Please log in to book a hotel.', 'info');
      navigate('/login', { state: { from: window.location.pathname } });
      return;
    }
    setSelectedHotel(hotel);
    setShowBookingModal(true);
  };

  const handleViewDetails = (hotel) => {
    navigate(`/hotels/${hotel.id}`);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={14}
        className={index < Math.floor(rating) ? 'text-yellow-500 fill-current' : 'text-gray-300'}
      />
    ));
  };

  return (
    <div className="min-h-screen bg-background">
      <Header /> {/* user and onLogout props removed */}
      <main className="pt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-primary/10 to-secondary/10 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Luxury Hotels in Uganda
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Discover world-class hospitality with our curated selection of premium hotels across Uganda
            </p>
          </div>
        </div>

        {/* Hotel Filters */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <HotelFilters onFiltersChange={handleFiltersChange} currentFilters={filters} />
        </div>

        {/* Hotels Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {isLoading ? (
            <p>Loading hotels...</p>
          ) : isError ? (
            <p>Error: {error?.message || 'Failed to load hotels.'}</p>
          ) : hotels.length === 0 ? (
            <p>No hotels found matching your criteria.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-8">
              {hotels.map((hotel) => (
                <div key={hotel.id} className="bg-card rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 group">
                  {/* Hotel Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={hotel.title_image} // Use title_image from API
                      alt={hotel.title} // Use title from API
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                      From UGX {hotel.price} {/* Assuming currency is UGX */}
                    </div>
                  </div>

                  {/* Hotel Info */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-xl font-semibold text-foreground mb-1">{hotel.title}</h3>
                        <div className="flex items-center text-muted-foreground text-sm mb-2">
                          <Icon name="MapPin" size={14} className="mr-1" />
                          {hotel.address}
                        </div>
                      </div>
                      {/* Rating and Reviews (mocked for now) */}
                      <div className="flex items-center">
                        {renderStars(hotel.rating || 4.0)}
                        <span className="ml-2 text-sm text-muted-foreground">
                          ({hotel.reviews || 0})
                        </span>
                      </div>
                    </div>

                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                      {hotel.description}
                    </p>

                    {/* Amenities (mocked for now) */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {(hotel.amenities || ["WiFi", "Parking"]).slice(0, 3).map((amenity) => (
                        <span
                          key={amenity}
                          className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full"
                        >
                          {amenity}
                        </span>
                      ))}
                      {(hotel.amenities || []).length > 3 && (
                        <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                          +{(hotel.amenities || []).length - 3} more
                        </span>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleViewDetails(hotel)}
                      >
                        <Icon name="Eye" size={16} className="mr-2" />
                        View Details
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => handleBookNow(hotel)}
                      >
                        <Icon name="Calendar" size={16} className="mr-2" />
                        Book Now
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalResults > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalResults={totalResults}
              resultsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>

        {/* Booking Modal */}
        {showBookingModal && selectedHotel && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-foreground">Book {selectedHotel.name}</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowBookingModal(false)}
                  >
                    <Icon name="X" size={20} />
                  </Button>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Available Rooms</h3>
                    <div className="space-y-3">
                      {/* Mock room types for now, as API doesn't provide this directly */}
                      {selectedHotel.rooms ? selectedHotel.rooms.map((room, index) => (
                        <div key={index} className="flex items-center justify-between p-4 border border-border rounded-lg">
                          <div>
                            <h4 className="font-medium text-foreground">{room.type}</h4>
                            <p className="text-sm text-muted-foreground">{room.available} rooms available</p>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-primary">
                              UGX {room.price}
                            </div>
                            <p className="text-sm text-muted-foreground">per night</p>
                          </div>
                        </div>
                      )) : (
                        <div key="default-room" className="flex items-center justify-between p-4 border border-border rounded-lg">
                          <div>
                            <h4 className="font-medium text-foreground">Standard Room</h4>
                            <p className="text-sm text-muted-foreground">5 rooms available</p>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-primary">
                              UGX {selectedHotel.price}
                            </div>
                            <p className="text-sm text-muted-foreground">per night</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowBookingModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button className="flex-1">
                      <Icon name="Calendar" size={16} className="mr-2" />
                      Proceed to Booking
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Hotels;
